<script lang="ts">
import { HelpCircle, Moon, RefreshCw, ShieldCheck, Sun, Users } from '@lucide/svelte';
import { onMount, type Component } from 'svelte';
import { capture, identifyUser, resetUser } from '$lib/analytics';
import * as Avatar from '$lib/components/ui/avatar/index.js';
import * as db from '$lib/db';
import { exportSitesCsv } from '$lib/format';
import { decode, encode, type ViewState } from '$lib/urlState';
import LazyLoadStatus from './components/common/LazyLoadStatus.svelte';
import Toasts from './components/common/Toasts.svelte';
import AuthModal from './components/modals/AuthModal.svelte';
import Confirm from './components/modals/Confirm.svelte';
import { auth } from './stores/auth.svelte';
import { batteryDiagnostics } from './stores/batteryDiagnostics.svelte';
import { cabinetInventory } from './stores/cabinetInventory.svelte';
import { cabinets } from './stores/cabinets.svelte';
import { draw } from './stores/draw.svelte';
import { fleet } from './stores/fleet.svelte';
import { form } from './stores/form.svelte';
import { loadUserPreferences } from './stores/preferences.svelte';
import { refresher } from './stores/refresh.svelte';
import { sites } from './stores/sites.svelte';
import { type Tab, ui } from './stores/ui.svelte';

onMount(() => {
  // Read initial URL state at mount (demoting localStorage to fallback)
  if (typeof window !== 'undefined' && window.location.search) {
    const params = new URLSearchParams(window.location.search);
    const decoded = decode(params);
    if (decoded.tab) ui.tab = decoded.tab;
    if (decoded.mode) fleet.mode = decoded.mode;
    if (decoded.basemap) {
      ui.basemap = decoded.basemap;
      fleet.basemap = decoded.basemap;
    }
    if (decoded.layers) fleet.applyControls({ layers: decoded.layers });
    // `sel` is per-tab: a `site:` prefix is a Map-tab site (MarkersLayer flies
    // to it and opens its popup once sites load), anything else is a Fleet
    // marker id, which FleetPins opens once its markers exist.
    if (decoded.sel?.startsWith('site:')) {
      const siteId = decoded.sel.slice('site:'.length);
      if (siteId) ui.select(siteId, 'url');
    } else if (decoded.sel) {
      fleet.selectedPin = decoded.sel;
    }
    // SiteDetail renders as soon as sites.byId resolves this.
    if (decoded.detail) ui.detailId = decoded.detail;
    if (decoded.viewport) {
      ui.setViewport(decoded.viewport.center, decoded.viewport.zoom);
      fleet.setViewport(decoded.viewport.center, decoded.viewport.zoom);
    }
  }

  // The app is sign-in-only: no table read is attempted before a session
  // exists (anon has no read path at all since
  // 20260821000000_require_auth_everywhere). startData() runs from the auth
  // effect below, once, as soon as a user is present.
  auth.init();
  document.addEventListener('keydown', onKey);
  return () => {
    unsubOverrides?.();
    unsubOverrides = null;
    document.removeEventListener('keydown', onKey);
  };
});

let dataStarted = false;
let unsubOverrides: (() => void) | null = null;

// Each view and overlay gets its own import boundary. In particular, the
// survey map's Leaflet stack is not evaluated until Map is requested; after
// that first visit SurveyMapView stays mounted and MapCanvas retains its
// viewport while inactive.
let DashboardView = $state<Component | null>(null);
let SurveyMapView = $state<Component<{ active: boolean }> | null>(null);
let FleetView = $state<Component | null>(null);
let AdminModal = $state<Component | null>(null);
let TeamPanel = $state<Component | null>(null);
let HelpModal = $state<Component | null>(null);
let SiteForm = $state<Component | null>(null);
let SiteDetail = $state<Component | null>(null);
let FleetOrientationCard = $state<Component | null>(null);
let BatteryDiagnosticsModal = $state<Component | null>(null);
let CabinetInventoryModal = $state<Component | null>(null);
let loadingTab = $state<Tab | null>(null);

async function loadTab(tab: Tab) {
  if (tab === 'dashboard') {
    if (!auth.canAccessDashboard) return;
    if (DashboardView) return;
    loadingTab = tab;
    DashboardView = (await import('./components/dashboard/DashboardView.svelte')).default;
  } else if (tab === 'map') {
    if (!auth.canAccessMap) return;
    if (SurveyMapView) return;
    loadingTab = tab;
    SurveyMapView = (await import('./components/map/SurveyMapView.svelte')).default;
  } else {
    if (FleetView) return;
    loadingTab = tab;
    FleetView = (await import('./components/fleet/FleetView.svelte')).default;
  }
  if (loadingTab === tab) loadingTab = null;
}

function loadAdmin() {
  if (!AdminModal)
    void import('./components/modals/AdminModal.svelte').then(
      ({ default: component }) => (AdminModal = component),
    );
}
function loadTeamPanel() {
  if (!TeamPanel)
    void import('./components/modals/TeamAccessModal.svelte').then(
      ({ default: component }) => (TeamPanel = component),
    );
}
function loadHelp() {
  if (!HelpModal)
    void import('./components/modals/HelpModal.svelte').then(
      ({ default: component }) => (HelpModal = component),
    );
}
function loadSiteForm() {
  if (!SiteForm)
    void import('./components/modals/SiteForm.svelte').then(
      ({ default: component }) => (SiteForm = component),
    );
}
function loadSiteDetail() {
  if (!SiteDetail)
    void import('./components/modals/SiteDetail.svelte').then(
      ({ default: component }) => (SiteDetail = component),
    );
}
function loadFleetOrientation() {
  if (!FleetOrientationCard)
    void import('./components/fleet/FleetOrientationCard.svelte').then(
      ({ default: component }) => (FleetOrientationCard = component),
    );
}
function loadBatteryDiagnostics() {
  if (!BatteryDiagnosticsModal)
    void import('./components/modals/BatteryDiagnosticsModal.svelte').then(
      ({ default: component }) => (BatteryDiagnosticsModal = component),
    );
}
function loadCabinetInventory() {
  if (!CabinetInventoryModal)
    void import('./components/modals/CabinetInventoryModal.svelte').then(
      ({ default: component }) => (CabinetInventoryModal = component),
    );
}

$effect(() => {
  if (batteryDiagnostics.selectedBatteryId) loadBatteryDiagnostics();
});
$effect(() => {
  if (cabinetInventory.selectedCabinetId) loadCabinetInventory();
});

if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__cabinetInventory = cabinetInventory;
  (window as unknown as Record<string, unknown>).__batteryDiagnostics = batteryDiagnostics;
}


// A denied deep link renders Fleet without mutating ui.tab or rewriting the
// URL (D3): the requested tab stays in the address bar, so an admin whose
// role RPC is still resolving — or a member pasting a Map link — both land
// on what they're allowed to see with the original link preserved.
const effectiveTab = $derived(
  (ui.tab === 'map' && !auth.canAccessMap) || (ui.tab === 'dashboard' && !auth.canAccessDashboard)
    ? 'fleet'
    : ui.tab,
);

let lastDeniedTab: Tab | null = null;
$effect(() => {
  if (!auth.user || !auth.roleReady) return; // D3: never gate before the DB role resolves
  if (effectiveTab !== ui.tab && ui.tab !== lastDeniedTab) {
    lastDeniedTab = ui.tab;
    capture('tab_denied', { tab: ui.tab, role: auth.role });
  }
  void loadTab(effectiveTab);
});

// The orientation's one-time trigger belongs outside its lazy boundary: it
// opens immediately on a first Fleet visit, then causes its own card to load.
$effect(() => {
  if (auth.user && ui.tab === 'fleet' && !ui.orientationSeen) {
    ui.orientationSeen = true;
    ui.orientationOpen = true;
  }
});

$effect(() => {
  if (ui.adminOpen) loadAdmin();
  if (ui.teamOpen) loadTeamPanel();
  if (ui.helpOpen) loadHelp();
  if (form.open) loadSiteForm();
  if (ui.detailId) loadSiteDetail();
  if (ui.orientationOpen) loadFleetOrientation();
});

function startData() {
  if (dataStarted) return;
  dataStarted = true;
  sites.hydrate();
  cabinets.load();
  sites.loadOverrides();
  unsubOverrides = db.subscribeOverrides((ro) => sites.setOverride(ro));
}

function stopData() {
  dataStarted = false;
  unsubOverrides?.();
  unsubOverrides = null;
  sites.reset();
}

// A2: Sync ViewState to URL with debounce via replaceState
let urlTimer: ReturnType<typeof setTimeout> | undefined;
$effect(() => {
  const currentTab = ui.tab;
  const currentMode = fleet.mode;
  const currentBasemap = currentTab === 'fleet' ? fleet.basemap : ui.basemap;
  const currentLayers = fleet.layers;
  const currentSel =
    currentTab === 'fleet'
      ? fleet.selectedPin
      : currentTab === 'map' && ui.selectedId
        ? `site:${ui.selectedId}`
        : null;
  // Not gated on the tab: the detail modal is a global overlay that survives a
  // tab switch, so the URL has to keep reflecting it while it's on screen.
  const currentDetail = ui.detailId;
  const currentViewport =
    currentTab === 'fleet'
      ? (fleet.mapViewport ?? ui.mapViewport)
      : (ui.mapViewport ?? fleet.mapViewport);

  const state: ViewState = {
    tab: currentTab,
    mode: currentMode,
    basemap: currentBasemap,
    layers: currentLayers,
    viewport: currentViewport ?? null,
    sel: currentSel,
    detail: currentDetail,
  };

  clearTimeout(urlTimer);
  urlTimer = setTimeout(() => {
    if (typeof window === 'undefined') return;
    const params = encode(state);
    const q = params.toString();
    const targetUrl = q ? `${window.location.pathname}?${q}` : window.location.pathname;
    history.replaceState(null, '', targetUrl);
  }, 300);
});

// Refetch sites when auth state resolves/changes — RLS returns more rows to
// an authenticated @powerhive user. Runs once at mount (anon) then on sign-in.
// Also drives PostHog identify/reset (original onAuthStateChange, L2164-2166).
let lastUserId: string | null = null;
$effect(() => {
  const u = auth.user;
  if (!u) {
    if (lastUserId) {
      resetUser();
      stopData();
      lastUserId = null;
    }
    return;
  }
  // Warm the map chunk while the user_role RPC is still in flight. The dynamic
  // import itself has no auth semantics — rendering stays gated on
  // auth.canAccessMap below — so downloading the Leaflet stack need not wait
  // for roleReady; otherwise the first basemap tile serializes behind the
  // role RPC round trip. loadTab still assigns the component once the role
  // resolves (repeat imports are cached module hits).
  if (ui.tab === 'map' && !SurveyMapView) void import('./components/map/SurveyMapView.svelte');
  startData();
  // §2.5b: a member's client never fetches the site table — the RLS policy
  // returns zero rows to them, so don't fire a doomed request or hold an
  // empty-state copy of data they can't have. Fleet reads fleet/solarSites,
  // not sites, so this costs the member's only tab nothing.
  if (auth.roleReady && auth.canAccessMap) void sites.load();
  if (u.id !== lastUserId) {
    identifyUser(u.id, { email: u.email });
    capture('login_success', { email_domain: u.email?.split('@')[1] ?? null });
    loadUserPreferences();
    lastUserId = u.id;
  }
});

// site_selected analytics (original L1443), fired on each new selection.
let lastSelected: string | null = null;
$effect(() => {
  const id = ui.selectedId;
  if (id && id !== lastSelected) capture('site_selected', { site_id: id, source: ui.selectSource });
  lastSelected = id;
});

function setTab(t: Tab) {
  if ((t === 'map' && !auth.canAccessMap) || (t === 'dashboard' && !auth.canAccessDashboard)) {
    // Denied tabs aren't rendered, so this is belt-and-braces — but never
    // mutate ui.tab here; effectiveTab derives the fallback.
    capture('tab_denied', { tab: t, role: auth.role });
    return;
  }
  capture('tab_switched', { tab: t });
  ui.tab = t;
  void loadTab(t);
}

const fleetSynced = $derived(
  fleet.lastUpdated === null ? 'syncing…' : new Date(fleet.lastUpdated).toLocaleTimeString(),
);

// C4: this used to just switch to the Dashboard tab and stop — an "Export"
// button that doesn't export. Reuses DashboardView's own CSV shape so the
// topbar and the dashboard's export produce the same file.
function exportCsv() {
  if (!auth.canExport) return;
  exportSitesCsv(sites.all);
  capture('csv_exported', {
    site_count: sites.all.length,
    authenticated: !!auth.user,
    source: 'topbar',
  });
}

// `?` (key or topbar button) opens the Fleet orientation card while on the
// Fleet tab, and the general HelpModal everywhere else — one shortcut, two
// destinations, arbitrated by tab so Fleet's rider-facing "how do I read
// this" question doesn't get buried in the roof-measurement walkthrough.
function openHelp() {
  if (ui.tab === 'fleet') {
    ui.orientationOpen = true;
    loadFleetOrientation();
  } else {
    ui.helpOpen = true;
    loadHelp();
  }
}

function onKey(e: KeyboardEvent) {
  const tag = (e.target as HTMLElement)?.tagName;
  const typing = tag === 'INPUT' || tag === 'TEXTAREA';
  if (e.key === 'Escape') {
    if (draw.active) draw.cancel();
    else if (form.open) form.close();
    else if (ui.detailId) ui.detailId = null;
    else if (ui.adminOpen) ui.adminOpen = false;
    else if (ui.teamOpen) ui.teamOpen = false;
    else if (ui.authOpen) ui.authOpen = false;
    else if (ui.orientationOpen) ui.orientationOpen = false;
    else if (ui.helpOpen) ui.helpOpen = false;
    else if (ui.layersSheetOpen) ui.layersSheetOpen = false;
    return;
  }
  if (typing) return;
  if (e.key === '/') {
    e.preventDefault();
    // `.sidebar input` is the Map tab's site search; Fleet has its own
    // place search (FleetSearch.svelte) instead — neither exists on the
    // Dashboard tab, where `/` is a no-op.
    const selector = ui.tab === 'fleet' ? '.fleet-search input' : '.sidebar input';
    document.querySelector<HTMLInputElement>(selector)?.focus();
  } else if (e.key === '?') {
    openHelp();
  } else if (e.key.toLowerCase() === 'h' && ui.tab === 'map' && auth.canAccessMap) {
    // Solar heatmap toggle — a Map-tab concept only; Fleet has its own
    // always-on heat layer driven by the selected mode, not this flag.
    ui.toggleHeat();
  } else if (e.key.toLowerCase() === 'd' && auth.user && ui.tab === 'map' && auth.canAccessMap) {
    // Roof drawing tool — Map-tab UI only (DrawBanner/MapInteractions).
    draw.start();
  }
}
</script>

{#if !auth.ready}
  <!-- Boot: session restore is in flight. Rendering nothing app-shaped here
       keeps a signed-out visitor from ever seeing the map frame. -->
  <div class="boot" aria-busy="true"><div class="boot-spark"></div></div>
{:else if !auth.user}
  <!-- Sign-in gate: the entire app lives behind a session. -->
  <AuthModal forced />
  <Toasts />
{:else}
<div class="app">
  <header class="topbar">
    <div class="brand">
      <svg class="spark" viewBox="0 0 60 70" aria-hidden="true"
        ><g fill="currentColor"
          ><polygon points="30,2 38,7 38,17 30,22 22,17 22,7" /><polygon points="42,12 50,17 50,27 42,32 34,27 34,17" /><polygon
            points="18,24 26,29 26,39 18,44 10,39 10,29"
          /><polygon points="34,30 42,35 42,45 34,50 26,45 26,35" /><polygon points="22,44 30,49 30,59 22,64 14,59 14,49" /><polygon
            points="40,48 47,52 47,61 40,65 33,61 33,52"
          /></g
        ></svg
      >
      <div class="brand-text">
        <div class="brand-name">powerhive <span>Kenya</span></div>
        <div class="brand-sub">Site Asset Survey</div>
      </div>
    </div>

    <nav aria-label="Primary">
      <div class="tabs" role="tablist" aria-label="View">
        {#if auth.canAccessMap}
          <button role="tab" aria-selected={ui.tab === 'map'} class:active={ui.tab === 'map'} onclick={() => setTab('map')} title="Map view">Map</button>
        {/if}
        {#if auth.canAccessDashboard}
          <button role="tab" aria-selected={ui.tab === 'dashboard'} class:active={ui.tab === 'dashboard'} onclick={() => setTab('dashboard')} title="Dashboard view">Dashboard</button>
        {/if}
        <button role="tab" aria-selected={ui.tab === 'fleet'} class:active={ui.tab === 'fleet'} onclick={() => setTab('fleet')} title="Live fleet telemetry">Fleet</button>
      </div>
    </nav>

    <div class="spacer"></div>

    <!-- B8: on the Fleet tab this is the fleet's live status, folded out of
         FleetHeader's own card so the map keeps the pixels and there is one
         status indicator on screen instead of two. -->
    {#if effectiveTab === 'fleet'}
      <div class="live fleet-live">
        <span class="dot" class:warn={fleet.status === 'stale'} class:idle={fleet.status !== 'ok' && fleet.status !== 'stale'}></span>
        {#if fleet.status === 'stale'}
          <span class="live-msg">{fleet.error ?? 'Fleet feed unavailable.'}</span>
        {:else if fleet.status === 'ok'}
          <span data-figure>{fleet.total.toLocaleString()} units</span>
          {#if auth.isAdmin && fleet.rejected}
            <span
              class="live-warn"
              data-figure
              title="{fleet.rejected} record{fleet.rejected === 1
                ? ''
                : 's'} dropped for unusable GPS — missing, zero, or outside Kenya"
              >· {fleet.rejected} wrong coordinates</span
            >
          {/if}
          <span class="live-ts" data-figure>· {fleetSynced}</span>
        {:else}
          Connecting…
        {/if}
      </div>
    {:else}
      <div class="live" title="Last synced">
        <span class="dot"></span>
        {sites.offlineTs ? 'Offline' : 'Live'}
      </div>
    {/if}

    <button class="pill desktop-only" onclick={() => refresher.run()} title="Refresh data" disabled={refresher.refreshing}
      ><RefreshCw size={14} class={refresher.refreshing ? 'animate-spin' : ''} /> Refresh</button
    >
    <button class="icon-btn mobile-only" onclick={() => refresher.run()} aria-label="Refresh data" title="Refresh data" disabled={refresher.refreshing}
      ><RefreshCw size={16} class={refresher.refreshing ? 'animate-spin' : ''} /></button
    >
    {#if auth.canExport}
      <button class="pill amber desktop-only" onclick={exportCsv} title="Export sites as CSV">↓ Export</button>
    {/if}

    <button class="icon-btn desktop-only" aria-label="Toggle dark mode" title="Dark mode" onclick={() => ui.toggleTheme()}>
      {#if ui.theme === 'dark'}<Sun size={16} />{:else}<Moon size={16} />{/if}
    </button>
    <button class="icon-btn" aria-label="Help and shortcuts" title="Help &amp; shortcuts" onclick={openHelp}><HelpCircle size={16} /></button>

    <!-- Non-admin Access panel (for commercial users and allowed roles; admins reach the directory via the Admin modal's Access tab) -->
    {#if auth.canAccessNonAdminPanel}
      <button class="icon-btn" aria-label="Team access" title="Access panel" onclick={() => { ui.teamOpen = true; loadTeamPanel(); }}><Users size={16} /></button>
    {/if}

    {#if auth.user}
      {#if auth.simulatedRole}
        <div
          class="sim-chip flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[length:var(--text-micro)] font-bold tracking-wide"
          style="background: {auth.simulatedRole === 'member' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(14, 165, 233, 0.15)'}; color: {auth.simulatedRole === 'member' ? '#10b981' : '#0ea5e9'}; border: 1px solid {auth.simulatedRole === 'member' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(14, 165, 233, 0.4)'};"
        >
          <span>Simulating {auth.simulatedRole === 'member' ? 'Member' : 'Commercial'}</span>
          <button
            type="button"
            class="ml-1 cursor-pointer font-bold uppercase tracking-wider text-[10px] px-1 py-0.2 rounded hover:bg-white/10"
            onclick={() => auth.setRoleOverride(null)}
            aria-label="Reset simulation"
            title="Reset simulation to real admin"
          >
            Reset
          </button>
        </div>
      {:else if auth.canAccessAdmin && auth.isRealAdmin}
        <button
          type="button"
          class="admin-btn flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[length:var(--text-micro)] font-bold tracking-wide transition-transform hover:scale-105 active:scale-95 cursor-pointer"
          style="background: rgba(245, 158, 11, 0.15); color: #f59e0b; border: 1px solid rgba(245, 158, 11, 0.35);"
          title="Open Admin Controls"
          onclick={() => {
            ui.adminOpen = true;
            loadAdmin();
          }}
        >
          <ShieldCheck size={12} />
          <span class="admin-label">Admin</span>
        </button>
      {/if}
      <button class="auth-btn" title={auth.user.email ?? ''} onclick={() => auth.signOut()}>
        <Avatar.Root>
          <Avatar.Image src={auth.avatarUrl} alt={auth.user.email ?? ''} referrerpolicy="no-referrer" />
          <Avatar.Fallback>{auth.initials}</Avatar.Fallback>
        </Avatar.Root>
      </button>
    {/if}
  </header>

  <main class="body">
    {#if auth.canAccessMap && SurveyMapView}
      <SurveyMapView active={effectiveTab === 'map'} />
    {:else if auth.canAccessMap && effectiveTab === 'map'}
      <LazyLoadStatus label="map" />
    {/if}
    {#if auth.canAccessDashboard && effectiveTab === 'dashboard' && DashboardView}
      <DashboardView />
    {:else if auth.canAccessDashboard && effectiveTab === 'dashboard'}
      <LazyLoadStatus label="dashboard" />
    {/if}
    {#if effectiveTab === 'fleet' && FleetView}
      <FleetView />
    {:else if effectiveTab === 'fleet'}
      <LazyLoadStatus label="fleet" />
    {/if}
  </main>
</div>

{#if auth.canAccessAdmin && AdminModal}
  <AdminModal />
{:else if auth.canAccessAdmin && ui.adminOpen}
  <LazyLoadStatus label="admin controls" overlay />
{/if}
{#if TeamPanel}
  <TeamPanel />
{:else if ui.teamOpen}
  <LazyLoadStatus label="team access" overlay />
{/if}
{#if SiteForm}
  <SiteForm />
{:else if form.open}
  <LazyLoadStatus label="site form" overlay />
{/if}
{#if SiteDetail}
  <SiteDetail />
{:else if ui.detailId}
  <LazyLoadStatus label="site details" overlay />
{/if}
<Confirm />
{#if HelpModal}
  <HelpModal />
{:else if ui.helpOpen}
  <LazyLoadStatus label="help" overlay />
{/if}
{#if FleetOrientationCard}
  <FleetOrientationCard />
{:else if ui.orientationOpen}
  <LazyLoadStatus label="fleet guide" overlay />
{/if}
{#if BatteryDiagnosticsModal}
  <BatteryDiagnosticsModal />
{:else if batteryDiagnostics.selectedBatteryId}
  <LazyLoadStatus label="battery diagnostics" overlay />
{/if}
{#if CabinetInventoryModal}
  <CabinetInventoryModal />
{:else if cabinetInventory.selectedCabinetId}
  <LazyLoadStatus label="cabinet inventory" overlay />
{/if}
<Toasts />
{/if}

<style>
  /* Hallmark · design-system: design.md v3.0.0 · genre: utilitarian
   * contrast: pass · mobile: pass · tokens: pass
   */
  .boot {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: var(--bg);
  }
  .boot-spark {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid var(--border);
    border-top-color: #faa61a;
    animation: boot-spin 0.8s linear infinite;
  }
  @keyframes boot-spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .boot-spark {
      animation: none;
    }
  }
  .app {
    position: fixed;
    inset: 0;
    display: flex;
    flex-direction: column;
    background: var(--bg);
    color: var(--fg);
    overflow: hidden;
  }
  .topbar {
    display: flex;
    align-items: center;
    gap: 18px;
    height: 60px;
    flex: none;
    padding: 0 20px;
    background: transparent;
    border-bottom: 1px solid var(--border);
    z-index: var(--z-topbar);
  }
  .brand {
    display: flex;
    align-items: center;
    gap: 11px;
  }
  .spark {
    width: 26px;
    height: 30px;
    color: #faa61a;
    flex: none;
  }
  .brand-name {
    font-family: 'Archivo', sans-serif;
    font-weight: 700;
    font-size: 15px;
    letter-spacing: -0.2px;
    white-space: nowrap;
  }
  .brand-name span {
    color: var(--muted);
    font-weight: 600;
  }
  .brand-sub {
    font-size: 10px;
    color: var(--muted);
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .tabs {
    display: flex;
    gap: 4px;
    background: var(--surface-2);
    padding: 4px;
    border-radius: 10px;
    border: 1px solid var(--border);
  }
  .tabs button {
    border: none;
    background: transparent;
    color: var(--muted);
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 7px;
    cursor: pointer;
  }
  .tabs button.active {
    background: var(--surface);
    color: var(--fg);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  }
  .spacer {
    flex: 1;
  }
  .live {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--muted);
  }
  .live .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex: none;
    background: #3fcf8e;
    box-shadow: 0 0 8px #3fcf8e;
  }
  .live .dot.warn {
    background: var(--destructive);
    box-shadow: none;
  }
  .live .dot.idle {
    background: var(--cyan);
    box-shadow: none;
  }
  .fleet-live {
    gap: 6px;
    max-width: 340px;
    min-width: 0;
    color: var(--fg);
  }
  .fleet-live [data-figure] {
    font-variant-numeric: tabular-nums;
  }
  .live-msg {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--fg);
  }
  .live-warn {
    color: var(--muted);
    text-decoration: underline dotted;
    text-underline-offset: 2px;
    cursor: help;
  }
  .live-ts {
    color: var(--muted);
    font-family: var(--font-mono, ui-monospace, monospace);
    font-size: 11px;
  }
  .mobile-only {
    display: none !important;
  }
  .icon-btn:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .pill:disabled {
    opacity: 0.6;
    cursor: default;
  }
  .icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 9px;
    color: var(--muted);
    cursor: pointer;
    text-decoration: none;
  }
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 34px;
    padding: 0 14px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 9px;
    color: var(--muted);
    font: inherit;
    font-size: 12.5px;
    font-weight: 500;
    cursor: pointer;
  }
  .pill.amber {
    background: #faa61a;
    border: none;
    color: #3a2600;
    font-weight: 600;
  }
  .auth-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 34px;
    min-width: 34px;
    padding: 0 12px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 9px;
    color: var(--fg);
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
  }
  .auth-btn:not(.signin) {
    width: 36px;
    height: 36px;
    padding: 4px;
    background: transparent;
    border: none;
    border-radius: 50%;
  }
  .body {
    position: relative;
    flex: 1;
    min-height: 0;
  }
  /* ---- phones: map is the hero; sidebar becomes a bottom sheet ---- */
  @media (max-width: 768px) {
    .topbar {
      gap: 8px;
      padding: 0 10px;
      height: 52px;
    }
    .brand-sub,
    .brand-name,
    .live,
    .desktop-only {
      display: none !important;
    }
    .mobile-only {
      display: flex !important;
    }
    .tabs button {
      padding: 6px 9px;
      font-size: 12px;
      white-space: nowrap;
    }
    /* Keep the account avatar reachable on narrow screens. The Admin action
       retains its shield icon and accessible title, while its label would push
       the signed-in Google photo beyond the viewport. */
    .admin-btn {
      width: 34px;
      height: 34px;
      justify-content: center;
      padding: 0;
    }
    .admin-label {
      display: none;
    }
  }
</style>
